<template>
  <div>
    <widget
      :id="'client-list-widget'"
      :title="$tc('phrases.customerList')"
      :search="true"
      :subscriber="subscriber"
      :button="true"
      :paginator="paginator"
      :show_per_page="true"
      :route_name="'/people'"
      color="primary"
      :button-text="$tc('phrases.addCustomer')"
      :key="widgetKey"
      @widgetAction="
        () => {
          showAddClient = true
        }
      "
    >
      <div class="md-layout md-gutter">
        <div class="md-layout-item md-size-100">
          <div class="export-buttons-container">
            <div class="filter-section">
              <md-button
                class="md-dense md-button-icon"
                @click="showFilter = !showFilter"
              >
                {{ $tc("words.filter") }}
                <md-icon>
                  {{
                    showFilter ? "keyboard_arrow_down" : "keyboard_arrow_left"
                  }}
                </md-icon>
              </md-button>
            </div>
            <div class="export-section">
              <md-button
                class="md-raised md-default"
                @click="exportDebts"
                :disabled="downloadingDebts"
              >
                <md-icon class="export-icon">
                  <Transition mode="out-in" name="fade">
                    <md-progress-spinner
                      v-if="downloadingDebts"
                      md-mode="indeterminate"
                      :md-diameter="21"
                      :md-stroke="3"
                    />
                    <span v-else>download</span>
                  </Transition>
                </md-icon>
                {{ $tc("phrases.exportCustomersDebts") }}
              </md-button>
              <md-button
                class="md-raised md-primary"
                @click="showExportModal = true"
                :disabled="downloadingCustomers"
              >
                <md-icon>download</md-icon>
                {{ $tc("phrases.exportAllCustomers") }}
              </md-button>
            </div>
          </div>
        </div>

        <div class="md-layout-item md-size-100 content-with-filter">
          <div v-if="showFilter" class="filter-overlay">
            <client-filter
              v-model="customerFilters"
              :agents="agentService.list"
              @apply="onFilterApply"
              @clear="onFilterClear"
            />
            <div v-if="agentService.list.length === 0" class="agent-warning">
              No agents loaded. Check console for errors.
            </div>
          </div>

          <div class="md-layout-item md-size-100">
            <!-- No customers message for agent filter -->
            <div
              v-if="selectedAgentId && people.list.length === 0"
              class="no-customers-container"
            >
              <md-icon class="no-customers-icon">people</md-icon>
              <div class="no-customers-title">
                No customers found for this agent
              </div>
              <div class="no-customers-subtitle">
                Try selecting a different agent or clear the filter
              </div>
            </div>

            <div v-if="people.list.length > 0">
              <md-table
                v-model="people.list"
                md-card
                style="margin-left: 0"
                md-sort="created_at"
                md-sort-order="desc"
                @md-sorted="onSort"
              >
                <md-table-row
                  slot="md-table-row"
                  slot-scope="{ item }"
                  @click="detail(item.id)"
                  style="cursor: pointer"
                >
                  <md-table-cell
                    :md-label="$tc('words.name')"
                    md-sort-by="name"
                  >
                    {{ item.name }} {{ item.surname }}
                    <md-chip
                      v-if="item.type === 'agent'"
                      class="md-primary device-chip"
                    >
                      {{ $tc("words.agent") }}
                    </md-chip>
                  </md-table-cell>

                  <md-table-cell :md-label="$tc('words.phone')">
                    {{
                      item.addresses.length > 0 ? item.addresses[0].phone : "-"
                    }}
                  </md-table-cell>

                  <md-table-cell
                    :md-label="$tc('words.city')"
                    md-sort-by="city"
                    class="hidden-xs"
                  >
                    {{
                      item.addresses.length > 0 && item.addresses[0].city
                        ? item.addresses[0].city.name
                        : "-"
                    }}
                  </md-table-cell>

                  <md-table-cell :md-label="$tc('words.isActive')">
                    {{ item.is_active ? $tc("words.yes") : $tc("words.no") }}
                  </md-table-cell>

                  <md-table-cell :md-label="$tc('words.device')">
                    <template v-if="item.devices.length > 0">
                      <md-chip
                        v-for="device in item.devices"
                        :key="device.device_id"
                        :class="[
                          'device-chip',
                          deviceChipClass(device.device_type),
                        ]"
                      >
                        <md-icon class="device-chip-icon">
                          {{ deviceIcon(device.device_type) }}
                        </md-icon>
                        <md-tooltip md-direction="top">
                          {{ device.device_serial }}
                          ({{ $tc(`words.${device.device_type}`) }})
                        </md-tooltip>
                        {{ truncateSerial(device.device_serial) }}
                      </md-chip>
                    </template>
                    <span v-else>-</span>
                  </md-table-cell>

                  <md-table-cell
                    :md-label="$tc('words.agent')"
                    md-sort-by="agent"
                  >
                    {{ getAgentName(item) }}
                  </md-table-cell>

                  <md-table-cell
                    :md-label="$tc('phrases.lastUpdate')"
                    md-sort-by="created_at"
                    class="hidden-xs"
                  >
                    {{ timeForTimeZone(item.lastUpdate) }}
                  </md-table-cell>
                </md-table-row>
              </md-table>
            </div>
          </div>
        </div>
      </div>
    </widget>

    <add-client-modal
      :showAddClient="showAddClient"
      @hideAddCustomer="() => (showAddClient = false)"
    />

    <!-- Updated Export Modal for Customer Export -->
    <md-dialog :md-active.sync="showExportModal" class="export-dialog">
      <md-dialog-title>{{ $tc("phrases.exportCustomers") }}</md-dialog-title>

      <md-dialog-content>
        <!-- Geographical Filters Row -->
        <div class="md-layout md-gutter">
          <div class="md-layout-item md-size-50 md-xsmall-size-100">
            <md-field>
              <label>{{ $tc("words.site") }}</label>
              <md-select v-model="exportFilters.miniGrid">
                <md-option value="">{{ $tc("words.all") }}</md-option>
                <md-option
                  v-for="miniGrid in miniGridService.list"
                  :key="miniGrid.id"
                  :value="miniGrid.name"
                >
                  {{ miniGrid.name }}
                </md-option>
              </md-select>
            </md-field>
          </div>
          <div class="md-layout-item md-size-50 md-xsmall-size-100">
            <city-autocomplete
              v-model="exportVillageId"
              :label="$tc('words.village')"
              @select="onVillageSelected"
            />
          </div>
        </div>

        <!-- Status, Device Type, and Format Row -->
        <div class="md-layout md-gutter">
          <div class="md-layout-item md-size-33 md-xsmall-size-100">
            <md-field>
              <label>{{ $tc("words.status") }}</label>
              <md-select v-model="exportFilters.isActive">
                <md-option value="">{{ $tc("words.all") }}</md-option>
                <md-option :value="true">{{ $tc("words.active") }}</md-option>
                <md-option :value="false">
                  {{ $tc("words.inactive") }}
                </md-option>
              </md-select>
            </md-field>
          </div>
          <div class="md-layout-item md-size-33 md-xsmall-size-100">
            <md-field>
              <label>{{ $tc("words.deviceType") }}</label>
              <md-select v-model="exportFilters.deviceType">
                <md-option value="">{{ $tc("words.all") }}</md-option>
                <md-option
                  v-for="device in deviceTypes"
                  :key="device.type"
                  :value="device.type"
                >
                  {{ device.display }}
                </md-option>
              </md-select>
            </md-field>
          </div>
          <div class="md-layout-item md-size-33 md-xsmall-size-100">
            <md-field>
              <label>{{ $tc("words.format") }}</label>
              <md-select v-model="exportFilters.format">
                <md-option value="csv">CSV</md-option>
                <md-option value="excel">Excel</md-option>
              </md-select>
            </md-field>
          </div>
        </div>
      </md-dialog-content>

      <md-progress-bar md-mode="indeterminate" v-if="downloadingCustomers" />

      <md-dialog-actions>
        <md-button class="md-raised" @click="showExportModal = false">
          {{ $tc("words.cancel") }}
        </md-button>
        <md-button
          class="md-primary md-raised"
          :disabled="downloadingCustomers"
          @click="exportCustomers"
        >
          {{ $tc("words.export") }}
        </md-button>
      </md-dialog-actions>
    </md-dialog>
  </div>
</template>

<script>
import { mapGetters } from "vuex"

import { Paginator } from "@/Helpers/Paginator.js"
import { notify } from "@/mixins/notify.js"
import { timing } from "@/mixins/timing.js"
import AddClientModal from "@/modules/Client/AddClientModal.vue"
import ClientFilter from "@/modules/Client/ClientFilter.vue"
import { resources } from "@/resources.js"
import { AgentService } from "@/services/AgentService.js"
import { CustomerExportService } from "@/services/CustomerExportService.js"
import { MainSettingsService } from "@/services/MainSettingsService.js"
import { MiniGridService } from "@/services/MiniGridService.js"
import { OutstandingDebtsExportService } from "@/services/OutstandingDebtsExportService.js"
import { People } from "@/services/PersonService.js"
import CityAutocomplete from "@/shared/CityAutocomplete.vue"
import { EventBus } from "@/shared/eventbus.js"
import Widget from "@/shared/Widget.vue"

const debounce = require("debounce")

export default {
  name: "Clients",
  mixins: [timing, notify],
  components: { AddClientModal, CityAutocomplete, Widget, ClientFilter },
  data() {
    return {
      subscriber: "client.list",
      people: new People(),
      paginator: new Paginator(resources.person.list),
      searchTerm: "",
      showAddClient: false,
      outstandingDebtsExportService: new OutstandingDebtsExportService(),
      customerExportService: new CustomerExportService(),
      mainSettingsService: new MainSettingsService(),
      agentService: new AgentService(),
      showExportModal: false,
      selectedAgentId: null,
      widgetKey: 0,
      showFilter: false,
      currentSortBy: "created_at",
      currentSortOrder: "desc",
      exportFilters: {
        format: "csv",
        isActive: "",
        miniGrid: "",
        village: "",
        deviceType: "",
      },
      exportVillageId: null,
      miniGridService: new MiniGridService(),
      isSearching: false,
      downloadingDebts: false,
      downloadingCustomers: false,
      customerFilters: {
        status: "all",
        agentId: null,
        totalPaidMin: null,
        totalPaidMax: null,
        cityId: null,
        latestPaymentFrom: null,
        latestPaymentTo: null,
        registrationFrom: null,
        registrationTo: null,
        deviceType: null,
      },
      // ISO-normalized filters; `customerFilters` holds raw Dates for the datepickers.
      appliedFilters: {
        status: "all",
        agentId: null,
        totalPaidMin: null,
        totalPaidMax: null,
        cityId: null,
        latestPaymentFrom: null,
        latestPaymentTo: null,
        registrationFrom: null,
        registrationTo: null,
        deviceType: null,
      },
    }
  },
  computed: {
    ...mapGetters({
      deviceTypes: "device/getDeviceTypes",
    }),
  },
  watch: {
    searchTerm: debounce(function () {
      this.handleSearch()
    }, 300),
  },

  mounted() {
    this.getClientList()

    // Load supporting data asynchronously without blocking UI
    this.loadSupportingData()

    EventBus.$on("pageLoaded", this.reloadList)
    EventBus.$on("searching", this.onSearchEvent)
    EventBus.$on("end_searching", this.onEndSearchEvent)
  },

  beforeDestroy() {
    EventBus.$off("pageLoaded", this.reloadList)
    EventBus.$off("searching", this.onSearchEvent)
    EventBus.$off("end_searching", this.onEndSearchEvent)
  },

  methods: {
    onVillageSelected(city) {
      this.exportFilters.village = city ? city.name : ""
    },
    handleSearch() {
      if (this.searchTerm.length > 2) {
        this.performSearch()
      }
    },

    onSort(sortData) {
      if (typeof sortData === "string") {
        this.currentSortBy = sortData
        if (
          this.currentSortBy === sortData &&
          this.currentSortOrder === "asc"
        ) {
          this.currentSortOrder = "desc"
        } else {
          this.currentSortOrder = "asc"
        }
      } else if (sortData && typeof sortData === "object") {
        this.currentSortBy = sortData.name || null
        this.currentSortOrder = sortData.type || "desc"
      } else {
        this.currentSortBy = null
        this.currentSortOrder = "desc"
      }

      // Build term object with sort parameters for pagination
      const term = {}
      if (this.currentSortBy) {
        const prefix = this.currentSortOrder === "desc" ? "-" : ""
        term.sort_by = `${prefix}${this.currentSortBy}`
      }
      if (this.selectedAgentId) {
        term.agent_id = this.selectedAgentId
      }

      // Emit EventBus event so Paginate.vue includes sort params in all subsequent pagination calls
      EventBus.$emit("loadPage", this.paginator, term)

      // Load the first page with sort applied
      this.getClientList(1)
    },

    async performSearch() {
      const term = this.searchTerm
      this.isSearching = true

      try {
        // Create search paginator
        const searchPaginator = new Paginator(resources.person.search)

        const params = { term }
        if (this.currentSortBy) {
          const prefix = this.currentSortOrder === "desc" ? "-" : ""
          params.sort_by = `${prefix}${this.currentSortBy}`
        }
        this.appendFilterParams(params)

        const response = await searchPaginator.loadPage(1, params)

        if (term !== this.searchTerm) return

        this.people.updateList(response.data)
        this.paginator = searchPaginator

        EventBus.$emit(
          "widgetContentLoaded",
          this.subscriber,
          this.people.list.length,
        )
      } catch (error) {
        console.error("Search error:", error)
      } finally {
        this.isSearching = false
      }
    },

    onSearchEvent(searchTerm) {
      this.searchTerm = searchTerm
    },

    onEndSearchEvent() {
      this.searchTerm = ""
      this.showAllEntries()
    },

    reloadList(subscriber, data) {
      if (subscriber !== this.subscriber) {
        return
      }
      // Always update with the returned data - the paginator already applied
      // sort and search parameters in the API request, so the data is correct
      this.people.updateList(data)
      EventBus.$emit(
        "widgetContentLoaded",
        this.subscriber,
        this.people.list.length,
      )
    },

    detail(id) {
      this.$router.push({ path: "/people/" + id })
    },

    async getClientList(pageNumber = 1) {
      const params = {}
      if (this.currentSortBy) {
        const prefix = this.currentSortOrder === "desc" ? "-" : ""
        params.sort_by = `${prefix}${this.currentSortBy}`
      }

      if (this.isSearching && this.searchTerm) {
        params.term = this.searchTerm
      }

      this.appendFilterParams(params)

      try {
        const response = await this.paginator.loadPage(pageNumber, params)
        this.people.updateList(response.data)

        // Keep widget content state in sync with the actual list length.
        // This ensures that after clearing a search (especially one that returned no results),
        // the customer list becomes visible again instead of staying empty while
        // the paginator still shows existing entries.
        EventBus.$emit(
          "widgetContentLoaded",
          this.subscriber,
          this.people.list.length,
        )

        if (this.selectedAgentId) {
          // Update pagination for filtered results
          this.updateFilteredPagination(response.data.length)
        }
      } catch (error) {
        console.error("Error loading client list:", error)
      }
    },

    updateFilteredPagination(count) {
      const filteredPaginator = new Paginator(resources.person.list)
      filteredPaginator.totalEntries = count
      filteredPaginator.currentPage = 1
      filteredPaginator.perPage = count
      filteredPaginator.totalPage = 1
      filteredPaginator.from = 1
      filteredPaginator.to = count

      this.$set(this.people, "paginator", filteredPaginator)
      this.widgetKey++
    },

    truncateSerial(serial) {
      if (!serial) return ""
      if (serial.length <= 12) return serial
      return (
        serial.substring(0, 6) + "..." + serial.substring(serial.length - 4)
      )
    },
    deviceIcon(deviceType) {
      const icons = {
        meter: "flash_on",
        solar_home_system: "wb_sunny",
        e_bike: "pedal_bike",
      }
      return icons[deviceType] || "devices"
    },
    deviceChipClass(deviceType) {
      const classes = {
        meter: "md-primary",
        solar_home_system: "md-accent",
        e_bike: "md-warn",
      }
      return classes[deviceType] || ""
    },

    showAllEntries() {
      this.searchTerm = ""
      this.paginator = new Paginator(resources.person.list)
      this.isSearching = false
      this.getClientList()
    },

    async exportDebts() {
      this.downloadingDebts = true

      try {
        const response =
          await this.outstandingDebtsExportService.exportOutstandingDebts()

        const blob = new Blob([response.data])
        const downloadUrl = window.URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = downloadUrl

        const contentDisposition = response.headers["content-disposition"]
        const filename =
          contentDisposition?.split("filename=")[1]?.replace(/['"]/g, "") ??
          "export.xlsx"

        a.download = filename
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(downloadUrl)
      } catch (e) {
        this.alertNotify(
          "error",
          "Error occured while exporting Customers' debts",
        )
      } finally {
        this.downloadingDebts = false
      }
    },

    async loadSupportingData() {
      // Run all these in parallel without blocking
      await Promise.allSettled([
        this.loadMainSettings(),
        this.loadAgents(),
        this.loadMiniGrids(),
      ])
    },

    async loadMainSettings() {
      try {
        const settings = await this.mainSettingsService.list()
        this.exportFilters.currency = settings.currency || "TZS"
        this.exportFilters.timeZone = "UTC"
      } catch (e) {
        console.error("Failed to load main settings:", e)
      }
    },

    async loadAgents() {
      try {
        const response = await this.agentService.repository.list()
        const agents = response.data.data || response.data
        this.agentService.updateList(agents)
      } catch (e) {
        console.error("Failed to load agents:", e)
      }
    },

    async loadMiniGrids() {
      try {
        await this.miniGridService.getMiniGrids()
      } catch (error) {
        console.error("Failed to load mini grids:", error)
      }
    },

    async exportCustomers() {
      this.downloadingCustomers = true

      try {
        const data = {
          format: this.exportFilters.format,
        }

        if (this.exportFilters.isActive !== "") {
          data.isActive = this.exportFilters.isActive
        }
        if (this.exportFilters.miniGrid !== "") {
          data.miniGrid = this.exportFilters.miniGrid
        }
        if (this.exportFilters.village !== "") {
          data.village = this.exportFilters.village
        }
        if (this.exportFilters.deviceType !== "") {
          data.deviceType = this.exportFilters.deviceType
        }
        if (this.selectedAgentId) {
          data.agent = this.selectedAgentId
        }

        const response = await this.customerExportService.exportCustomers(data)

        const blob = new Blob([response.data])
        const downloadUrl = window.URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = downloadUrl

        const contentDisposition = response.headers["content-disposition"]
        const filename =
          contentDisposition?.split("filename=")[1]?.replace(/['"]/g, "") ??
          (this.exportFilters.format === "excel" ? "export.xlsx" : "export.csv")

        a.download = filename
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(downloadUrl)
        this.alertNotify("success", "Customers exported successfully!")
        this.showExportModal = false
      } catch (e) {
        this.alertNotify("error", "Error occurred while exporting customers")
      } finally {
        this.downloadingCustomers = false
      }
    },

    buildFilterParams() {
      const params = {}

      // Agent
      if (this.appliedFilters.agentId) {
        this.selectedAgentId = this.appliedFilters.agentId
        params.agent_id = this.appliedFilters.agentId
      } else if (this.selectedAgentId) {
        params.agent_id = this.selectedAgentId
      }

      // Active / inactive
      if (this.appliedFilters.status === "active") {
        params.active_customer = 1
      } else if (this.appliedFilters.status === "inactive") {
        params.active_customer = 0
      }

      // City / village
      if (this.appliedFilters.cityId) {
        params.city_id = this.appliedFilters.cityId
      }

      // Total paid interval
      if (this.appliedFilters.totalPaidMin != null) {
        params.total_paid_min = this.appliedFilters.totalPaidMin
      }
      if (this.appliedFilters.totalPaidMax != null) {
        params.total_paid_max = this.appliedFilters.totalPaidMax
      }

      // Latest payment date
      if (this.appliedFilters.latestPaymentFrom) {
        params.latest_payment_from = this.appliedFilters.latestPaymentFrom
      }
      if (this.appliedFilters.latestPaymentTo) {
        params.latest_payment_to = this.appliedFilters.latestPaymentTo
      }

      // Registration date
      if (this.appliedFilters.registrationFrom) {
        params.registration_from = this.appliedFilters.registrationFrom
      }
      if (this.appliedFilters.registrationTo) {
        params.registration_to = this.appliedFilters.registrationTo
      }

      // Device / appliance type
      if (this.appliedFilters.deviceType) {
        params.device_type = this.appliedFilters.deviceType
      }

      return params
    },

    appendFilterParams(params) {
      Object.assign(params, this.buildFilterParams())
    },

    onFilterApply(payload) {
      // Build params from the ISO payload, never the raw Dates in customerFilters.
      this.appliedFilters = payload

      // Ensure paginator uses filters for all subsequent pages.
      // Let Paginate.vue handle the actual loading to avoid duplicate requests.
      const term = {}
      if (this.currentSortBy) {
        const prefix = this.currentSortOrder === "desc" ? "-" : ""
        term.sort_by = `${prefix}${this.currentSortBy}`
      }
      Object.assign(term, this.buildFilterParams())

      EventBus.$emit("loadPage", this.paginator, term)

      this.showFilter = false
    },

    onFilterClear(payload) {
      this.customerFilters = {
        status: "all",
        agentId: null,
        totalPaidMin: null,
        totalPaidMax: null,
        cityId: null,
        latestPaymentFrom: null,
        latestPaymentTo: null,
        registrationFrom: null,
        registrationTo: null,
        deviceType: null,
      }
      this.appliedFilters = payload
      this.selectedAgentId = null
      const term = {}
      if (this.currentSortBy) {
        const prefix = this.currentSortOrder === "desc" ? "-" : ""
        term.sort_by = `${prefix}${this.currentSortBy}`
      }
      // No extra filters; just re-load base list via paginator
      EventBus.$emit("loadPage", this.paginator, term)
    },

    getAgentName(client) {
      if (
        client.agent_sold_appliance &&
        client.agent_sold_appliance.assigned_appliance &&
        client.agent_sold_appliance.assigned_appliance.agent
      ) {
        const agentId = client.agent_sold_appliance.assigned_appliance.agent.id
        const agent = this.agentService.list.find((a) => a.id === agentId)
        if (agent) {
          return agent.person
            ? `${agent.person.name} ${agent.person.surname}`
            : agent.email || "-"
        }
      }
      return "-"
    },
  },
}
</script>

<style lang="scss" scoped>
.md-app {
  min-height: 100vh;
  border: 1px solid rgba(#000, 0.12);
}

.md-drawer {
  width: 230px;
  max-width: calc(100vw - 125px);
}

.export-buttons-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding: 0 1rem;
}

.filter-section {
  flex: 1;
  max-width: 300px;
}

.filter-expanded-section {
  background-color: #f5f5f5;
  padding: 1rem;
  border-radius: 4px;
  margin-top: 1rem;
  border: 1px solid #e0e0e0;
}

.export-section {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  gap: 1rem;
  margin-left: auto;
}

.download-debts-span {
  font-size: medium;
  font-weight: 500;
}

.export-dialog {
  min-width: min(600px, 100vw);
}

.export-dialog .md-dialog-content {
  padding: 20px;
}

.content-with-filter {
  position: relative;
}

.filter-overlay {
  position: sticky;
  width: 40%;
  margin-left: 1rem;
  min-width: 300px;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
  display: flex;
  justify-content: flex-start;
}

.agent-warning {
  color: red;
  font-size: 12px;
  margin-top: 0.5rem;
}

.no-customers-container {
  text-align: center;
  padding: 2rem;
  background: #f5f5f5;
  border-radius: 4px;
  margin: 1rem 0;
}

.no-customers-icon {
  font-size: 48px;
  color: #ccc;
  margin-bottom: 1rem;
}

.no-customers-title {
  color: #666;
  font-size: 18px;
  margin-bottom: 0.5rem;
}

.no-customers-subtitle {
  color: #999;
  font-size: 14px;
}

.fade-enter-active .fade-leave-active {
  transition: opacity ease;
}

.device-chip {
  margin: 2px;
  height: 26px;
  font-size: 12px;
  cursor: default;
  display: inline-flex;
  align-items: center;

  .device-chip-icon {
    font-size: 14px !important;
    margin-right: 4px;
    color: inherit !important;
    vertical-align: middle;
  }
}
</style>
